import React, { useEffect, useRef, useState } from 'react';
import { useAddCalendarMeetingLiveChatMutation, useGetCalendarMeetingLiveChatQuery } from '../../api/calendar.js';
import BaseCard from '../../components/misc/BaseCard.jsx';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { useErrorToast } from '../../hooks/useToast.jsx';
import IconAi from '../images/IconAi.jsx';
import IconSend from '../images/IconSend.jsx';

const DuringMeeting = ({ id, started, ended }) => {
    const [addCalendarMeetingLiveChat, { isLoading, error }] = useAddCalendarMeetingLiveChatMutation();
    const { data: liveChatData, error: fetchError, isLoading: isLoadingChat } = useGetCalendarMeetingLiveChatQuery(id);

    const [newMessage, setNewMessage] = useState('');
    const [isFirstTyped, setIsFirstTyped] = useState(false);
    const lastMessageRef = useRef(null);

    const scrollToLastMessage = () => {
        if (lastMessageRef.current) {
            lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    useEffect(() => {
        scrollToLastMessage();
    }, [liveChatData]);

    useEffect(() => {
        if (fetchError) {
            useErrorToast('An error occurred while loading the chat messages.');
        }
    }, [fetchError]);

    const handleInputChange = (e) => {
        const { value } = e.target;
        setNewMessage(value);

        if (value.trim().length > 0 && !isFirstTyped) {
            setIsFirstTyped(true);
        }

        if (value.trim().length === 0) {
            setIsFirstTyped(false);
        }
    };

    const handleSendMessage = async () => {
        if (newMessage.trim()) {
            try {
                await addCalendarMeetingLiveChat({ id, data: { message: newMessage } });

                setNewMessage('');
                setIsFirstTyped(false);
            } catch (error) {
                console.error('Failed to send message:', error);
            }
        }
    };

    const renderLiveChats = () => {
        return liveChatData?.response?.liveChats?.map((chat, index) => (
            <BaseCard
                key={chat.id}
                ref={index === liveChatData.response.liveChats.length - 1 ? lastMessageRef : undefined}
                className={`m-0 mb-6 p-4 bg-theme1-white w-[90%] ${chat.role !== 'assistant' ? 'self-end text-theme1-primary2' : 'bg-theme1-goldLight border-theme1-goldDark'}`}
            >
                {chat.role === 'assistant' && (
                    <IconAi className='stroke-theme1-gold flex-shrink-0 w-[30px] float-left mr-3 mt-1' />
                )}
                <strong className={`${chat.role === 'assistant' ? 'text-theme1-primary2' : ''}`}>
                    {chat.role === 'assistant' ? 'Assistant' : 'You'}:
                </strong>{' '}
                <span className={`${chat.role === 'assistant' ? 'text-theme1-primary2' : ''}`}>{chat.message}</span>
                <div className='text-xs text-gray-500 mt-1 text-right'>
                    {new Date(chat.created_at).toLocaleTimeString()}
                </div>
            </BaseCard>
        ));
    };

    return (
        <div
            className={`max-w-3xl w-full h-full flex flex-col flex-grow justify-between mx-auto ${
                !started ? 'p-6' : ended ? 'p-6' : 'py-0'
            }`}
        >
            <div className='text-xl font-bold text-theme1-primary mb-4 px-8'>AI-Powered Search</div>

            <div className='mt-auto flex flex-col'>
                <div className='flex-grow flex flex-col overflow-auto max-h-[550px] px-4'>
                    <div className='mt-auto flex flex-col relative pb-5'>
                        {renderLiveChats()}

                        {isLoading && <ThreeDotsLoader className='absolute right-0 bottom-2.5' />}

                        {error && (
                            <div className='text-red-500 text-sm mt-2 text-right mb-2'>
                                {error?.data?.message
                                    ? error.data.message
                                    : 'Message failed to send. Please try again.'}
                            </div>
                        )}
                    </div>
                </div>
                <div className='bg-theme1-tan rounded-t-4xl p-4 flex gap-2'>
                    <div className='flex-grow px-5 border border-theme1-border1 rounded-4xl bg-white'>
                        <textarea
                            className={`w-full pt-[12px] pb-[12px] h-[49px] max-h-[66px] focus:outline-none block bg-transparent mt-[2px] mb-[2px] flex-grow ${
                                isLoading ? 'text-gray-400' : ''
                            }`}
                            placeholder='Search within meeting context...'
                            value={newMessage}
                            onChange={handleInputChange}
                            style={{ resize: 'none' }}
                            disabled={isLoading}
                        />
                    </div>
                    <button
                        className={`btn rounded-4xl w-[55px] h-[55px] bg-theme1-primary text-white flex items-center justify-center ${
                            !isFirstTyped || isLoading ? 'hidden' : ''
                        } ${!newMessage.trim() || isLoading ? 'bg-theme1-disabled' : ''}`}
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || isLoading}
                    >
                        <IconSend className='fill-white w-[30px] h-[30px] -ml-1' />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DuringMeeting;
